import React from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import PicklingLogoMark from '../../../components/PicklingLogoMark';

const SessionCard = ({ session, onCopyLink, onDelete }) => {
  const navigate = useNavigate();
  const eventType = String(session?.eventType || 'open_game').trim().toLowerCase();
  const showActions = !['tournament', 'ad'].includes(eventType);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date?.toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      weekday: 'short'
    });
  };

  const formatTime = (timeString) => {
    return timeString;
  };

  const getSportIcon = (sport) => {
    const icons = {
      'pickleball': 'Dumbbell',
      'tennis': 'Trophy',
      'badminton': 'Target',
      'table-tennis': 'Circle'
    };
    return icons?.[sport] || 'Activity';
  };

  const EVENT_TYPE_STYLES = {
    open_game: { color: 'var(--color-primary)' },
    lesson: { color: '#2563eb' },
    training: { color: '#16a34a' },
    tournament: { color: '#f59e0b' },
    ad: { color: '#ec4899' },
  };

  const handleViewDetails = () => {
    if (eventType === 'lesson' || eventType === 'training') {
      navigate(`/session-list-dashboard/lesson-training-detail/${encodeURIComponent(session?.id)}`);
      return;
    }
    navigate(`/session-management-panel?id=${session?.id}`);
  };

  const handleEdit = () => {
    const eventType = String(session?.eventType || 'open_game').trim().toLowerCase();
    if (eventType === 'open_game') {
      navigate(`/session-creation-wizard?id=${session?.id}`);
      return;
    }
    navigate(`/session-list-dashboard/event-create/${encodeURIComponent(eventType)}?id=${session?.id}`);
  };

  const handleCopyLink = () => {
    const link = `${window.location?.origin}/public-session-page?token=${session?.token}`;
    navigator.clipboard?.writeText(link);
    if (onCopyLink) {
      onCopyLink(session);
    }
  };

  const handleDelete = () => {
    if (onDelete) {
      onDelete(session);
    }
  };

  return (
    <div className="group bg-card border border-border rounded-xl p-4 md:p-6 shadow-sm hover:shadow-md hover:border-primary/20 transition-athletic">
      <div className="flex flex-col gap-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3 flex-1 min-w-0">
            <div className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 bg-muted">
              {(() => {
                const eventType = String(session?.eventType || 'open_game').trim().toLowerCase();
                const style = EVENT_TYPE_STYLES[eventType] || EVENT_TYPE_STYLES.open_game;
                return (
                  <div
                    className="w-10 h-10 rounded-full flex items-center justify-center text-xs font-bold text-white"
                    style={{ background: style.color }}
                  >
                    <PicklingLogoMark size={18} className="text-white" />
                  </div>
                );
              })()}
            </div>
            
            <div className="flex-1 min-w-0">
              <h3 className="text-lg md:text-xl font-heading font-semibold text-foreground mb-1 line-clamp-2">
                {session?.title}
              </h3>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm caption text-muted-foreground">
                <span className="inline-flex items-center gap-1 whitespace-nowrap">
                  <Icon name="Calendar" size={14} />
                  {formatDate(session?.date)}
                </span>
                <span className="inline-flex items-center gap-1 whitespace-nowrap">
                  <Icon name="Clock" size={14} />
                  {formatTime(session?.startTime)} - {formatTime(session?.endTime)}
                </span>
              </div>
            </div>
          </div>

          <button
            onClick={handleDelete}
            className="p-2 rounded-md text-muted-foreground hover:text-error hover:bg-error/10 transition-athletic active-press flex-shrink-0"
            aria-label="세션 삭제"
          >
            <Icon name="Trash2" size={20} />
          </button>
        </div>

        {session?.venue && (
          <div className="flex items-center gap-2 text-sm caption text-muted-foreground min-w-0">
            <Icon name="MapPin" size={14} />
            <span className="truncate">{session?.venue}</span>
          </div>
        )}

        <div className="pt-4 border-t border-border flex flex-col sm:flex-row gap-2">
          {showActions && (
            <Button
              variant="default"
              size="sm"
              iconName="Eye"
              iconPosition="left"
              onClick={handleViewDetails}
              fullWidth
              className="sm:flex-1"
            >
              상세보기
            </Button>
          )}
          
          <Button
            variant="outline"
            size="sm"
            iconName="Edit"
            iconPosition="left"
            onClick={handleEdit}
            fullWidth
            className="sm:flex-1"
          >
            수정
          </Button>

          {showActions && (
            <Button
              variant="outline"
              size="sm"
              iconName="Link"
              iconPosition="left"
              onClick={handleCopyLink}
              fullWidth
              className="sm:flex-1"
            >
              링크 복사
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default SessionCard;